import { type ChordEvent, chordFromEvent } from './chord.js';
import type { CommandRegistry } from './command-registry.js';
import type { ScopeStack } from './scope-stack.js';
import { shouldIgnoreChord } from './target.js';

export type DispatchEvent = ChordEvent & {
  target: unknown;
  isComposing?: boolean;
  preventDefault?: () => void;
};

export type DispatchResult = 'ignored' | 'pending' | 'handled' | 'unmatched';

/** Ventana para completar una secuencia como `g m`. Suficiente para teclear sin prisa. */
const SEQUENCE_MS = 1000;

/**
 * Traduce eventos de teclado en comandos. Vive fuera de React para poder probar la
 * resolución completa (filtro, scope, secuencia) sin montar un árbol.
 */
export class HotkeyDispatcher {
  private pending = '';
  private pendingAt = 0;

  constructor(
    private readonly registry: CommandRegistry,
    private readonly scopes: ScopeStack,
    private readonly now: () => number = Date.now,
  ) {}

  handle(event: DispatchEvent): DispatchResult {
    const chord = chordFromEvent(event);
    const target = event.target as Parameters<typeof shouldIgnoreChord>[1];
    if (shouldIgnoreChord(chord, target, event.isComposing ?? false)) return 'ignored';

    const chain = this.scopes.chain();
    const keys = this.withPending(chord);

    const command = this.registry.resolve(keys, chain);
    if (command !== null) {
      this.pending = '';
      event.preventDefault?.();
      command.run();
      return 'handled';
    }

    if (this.registry.isSequencePrefix(keys, chain)) {
      this.pending = keys;
      this.pendingAt = this.now();
      event.preventDefault?.();
      return 'pending';
    }

    this.pending = '';
    return 'unmatched';
  }

  private withPending(chord: string): string {
    if (this.pending === '') return chord;
    const expired = this.now() - this.pendingAt > SEQUENCE_MS;
    if (expired) {
      this.pending = '';
      return chord;
    }
    return `${this.pending} ${chord}`;
  }
}
